import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useSeo from '../hooks/useSeo';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import {
  BookOpen, Target, ListChecks, GraduationCap, ArrowLeft, ArrowRight, ChevronRight,
  Cog, Map, Users, Compass, Sparkles, ClipboardList, NotebookPen,
} from 'lucide-react';
import { META_LIVRET, COMPETENCES_LIVRET } from '../data/livretApprentissageA';
import AudienceContextBanner from '../components/AudienceContextBanner';
import { useApp } from '../contexts/AppContext';

const COMP_ICONS = { C1: Cog, C2: Map, C3: Users, C4: Compass };
const COMP_COLORS = { C1: '#0891b2', C2: '#7c3aed', C3: '#dc2626', C4: '#16a34a' };
const VALID_CATS = ['C1', 'C2', 'C3', 'C4'];

/**
 * Vue interne : re-monte à chaque changement de `hashCode` via key.
 * → useState est donc initialisé depuis le hash, pas besoin de useEffect setState.
 */
function LivretView({ hashCode }) {
  const hashCat = hashCode ? hashCode.split('-')[0] : null;
  const initialTab = hashCat && VALID_CATS.includes(hashCat) ? hashCat : 'C1';
  const initialItem = hashCode && hashCat && VALID_CATS.includes(hashCat) ? hashCode : undefined;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [openItem, setOpenItem] = useState(initialItem);

  // Scroll vers l'item ouvert au montage (DOM only, pas de setState)
  useEffect(() => {
    if (!openItem) return undefined;
    const t = window.setTimeout(() => {
      const el = document.querySelector(`[data-testid="sous-comp-${openItem}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 320);
    return () => window.clearTimeout(t);
  }, [openItem, activeTab]);

  const totalSousComp = COMPETENCES_LIVRET.reduce((acc, c) => acc + c.sousCompetences.length, 0);

  return (
    <LivretLayout
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      openItem={openItem}
      setOpenItem={setOpenItem}
      totalSousComp={totalSousComp}
    />
  );
}

export default function LivretApprentissageA() {
  const location = useLocation();
  const hashCode = (location.hash || '').replace('#', '').trim();

  useSeo({
    title: "Livret d'apprentissage Catégorie A — 4 compétences détaillées (objectifs, critères, leçons)",
    description: "Référentiel REMC complet : 4 compétences moto (C1 à C4) avec 38 sous-compétences détaillées. Pour chaque item : objectif d'apprentissage, critères de réalisation observables, leçon-type pour bien progresser. Outil de référence ECSR.",
    path: '/enseignant/livret-apprentissage',
    noindex: true,
  });

  // key={hashCode} force le remount → useState ré-initialisé depuis le hash
  return <LivretView key={hashCode || 'root'} hashCode={hashCode} />;
}

function LivretLayout({ activeTab, setActiveTab, openItem, setOpenItem, totalSousComp }) {
  // Phase 31 : la carte "Articuler le livret / CCS" en bas doit rester réservée
  // aux enseignants/admin. Les élèves ne doivent pas être renvoyés vers du
  // contenu prépa jury CCS depuis leur livret.
  const { user } = useApp();
  const isTeacherOrAdmin = !!(user && (user.role === 'enseignant' || user.role === 'admin' || user.is_admin));

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="livret-apprentissage-page">
      {/* En-tête */}
      <header className="border-b border-border bg-gradient-to-br from-primary/5 via-background to-background py-8 px-4 md:px-6">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary inline-flex items-center gap-1" data-testid="back-home">
              <ArrowLeft className="w-3 h-3" />
              Accueil
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span>Livret d&apos;apprentissage A</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 space-y-2">
              <Badge variant="secondary" className="text-[10px] uppercase tracking-wider" data-testid="badge-source">
                {META_LIVRET.source}
              </Badge>
              <h1 className="text-2xl md:text-4xl font-bold leading-tight flex items-center gap-3">
                <ClipboardList className="w-8 h-8 text-primary flex-shrink-0" />
                {META_LIVRET.titre}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground max-w-3xl">{META_LIVRET.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 md:gap-3 text-center">
              <div className="rounded-lg border border-border bg-card px-3 py-2 min-w-[100px]">
                <div className="text-xl font-bold text-primary" data-testid="stat-competences">{COMPETENCES_LIVRET.length}</div>
                <div className="text-[10px] text-muted-foreground uppercase">compétences</div>
              </div>
              <div className="rounded-lg border border-border bg-card px-3 py-2 min-w-[100px]">
                <div className="text-xl font-bold text-primary" data-testid="stat-sous-comp">{totalSousComp}</div>
                <div className="text-[10px] text-muted-foreground uppercase">sous-compétences</div>
              </div>
            </div>
          </div>

          {/* CTA Auto-évaluation élève — pont vers MonLivretMoto */}
          <div
            className="mt-2 rounded-xl border-2 p-3 sm:p-4 flex items-center gap-3 flex-wrap"
            style={{ borderColor: 'hsl(var(--primary) / 0.3)', background: 'hsl(var(--primary) / 0.06)' }}
            data-testid="cta-auto-eval"
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0"
              style={{ background: 'hsl(var(--primary))' }}
              aria-hidden="true"
            >
              <NotebookPen className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black uppercase tracking-wider text-primary">Élève — Mon parcours</div>
              <p className="text-sm font-bold leading-snug">
                Auto-évalue-toi sur chaque compétence et suis ta progression.
              </p>
            </div>
            <Button asChild size="sm" data-testid="cta-mon-livret-moto" className="gap-1.5">
              <Link to="/mon-livret-moto">
                <NotebookPen className="w-4 h-4" /> Faire mon auto-évaluation
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 mt-6">
        {/* Bandeau d'adaptation par audience (visible pour les 3 publics) */}
        <AudienceContextBanner
          pageHint="Le livret d'apprentissage REMC sert les 3 publics moto : apprendre, enseigner, citer en oral CCS."
          hideOnEmpty
          contexts={{
            eleve: {
              title: 'Connaître les compétences attendues pour ton permis',
              bullets: [
                'Découvre les 4 compétences (C1-C4) que tu dois acquérir pour réussir ton permis A.',
                'Suis ta progression item par item via l\'auto-évaluation.',
                'Chaque sous-compétence a son objectif, ses critères et sa leçon-type.',
              ],
              cta: 'Faire mon auto-évaluation',
              ctaTo: '/mon-livret-moto',
            },
            enseignant: {
              title: 'Structurer ton enseignement sur la trame officielle REMC',
              bullets: [
                'Aligne tes objectifs de séance sur les sous-compétences C1-a → C4-g.',
                'Utilise les critères observables pour évaluer tes élèves de façon objective.',
                'Réutilise les leçons-types comme canevas pour préparer tes cours.',
              ],
              cta: 'Voir le canevas pédagogique',
              ctaTo: '/enseignant/canevas-pedagogique',
            },
            'futur-enseignant': {
              title: 'Maîtriser le REMC moto pour ton oral CCS',
              bullets: [
                'Mémorise la structure 4 compétences × 38 sous-compétences (citation jury fréquente).',
                'Sache associer chaque sous-compétence à son niveau GDE et son aspect (Cog/Soc/etc.).',
                'Pour ton oral, ancre toujours ton objectif sur le code REMC officiel (ex: C2-d).',
              ],
              cta: 'Aller au Hub Préparation CCS',
              ctaTo: '/preparation-ccs',
            },
          }}
          className="mb-4"
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto gap-1" data-testid="competences-tabs">
            {COMPETENCES_LIVRET.map((c) => {
              const Icon = COMP_ICONS[c.code] || BookOpen;
              return (
                <TabsTrigger
                  key={c.code}
                  value={c.code}
                  className="flex-col items-start gap-1 h-auto py-2.5 text-left whitespace-normal min-w-0 w-full"
                  data-testid={`tab-${c.code}`}
                  style={activeTab === c.code ? { borderColor: COMP_COLORS[c.code] } : {}}
                >
                  <div className="flex items-center gap-2 w-full min-w-0">
                    <Icon className="w-4 h-4 flex-shrink-0" style={{ color: COMP_COLORS[c.code] }} />
                    <span className="font-bold" style={{ color: COMP_COLORS[c.code] }}>{c.code}</span>
                    <Badge variant="outline" className="ml-auto text-[10px] flex-shrink-0">{c.sousCompetences.length} items</Badge>
                  </div>
                  <span className="text-[10px] text-muted-foreground line-clamp-2 leading-tight w-full break-words whitespace-normal">
                    {c.titre.replace(c.code + ' — ', '').split('—')[0].trim()}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {COMPETENCES_LIVRET.map((c) => (
            <TabsContent key={c.code} value={c.code} className="space-y-4">
              <Card style={{ borderTop: `3px solid ${COMP_COLORS[c.code]}` }}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div
                      className="w-12 h-12 rounded-md flex items-center justify-center font-bold text-white text-sm flex-shrink-0"
                      style={{ backgroundColor: COMP_COLORS[c.code] }}
                    >
                      {c.code}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-base leading-tight">{c.titre}</CardTitle>
                      <CardDescription className="mt-1.5">{c.contexte}</CardDescription>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-[10px]">Niveau GDE {c.niveauGDE}</Badge>
                        <Badge variant="outline" className="text-[10px]">{c.sousCompetences.length} sous-compétences</Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Accordion
                    type="single"
                    collapsible
                    className="space-y-2"
                    data-testid={`accordion-${c.code}`}
                    value={openItem}
                    onValueChange={setOpenItem}
                  >
                    {c.sousCompetences.map((sc) => (
                      <AccordionItem
                        key={sc.code}
                        value={sc.code}
                        className="border border-border rounded-md overflow-hidden"
                        data-testid={`sous-comp-${sc.code}`}
                      >
                        <AccordionTrigger className="px-3 py-2.5 hover:no-underline hover:bg-muted/30">
                          <div className="flex flex-1 items-start gap-3 text-left">
                            <Badge
                              variant="default"
                              className="font-mono text-[10px] flex-shrink-0"
                              style={{ backgroundColor: COMP_COLORS[c.code] }}
                            >
                              {sc.code}
                            </Badge>
                            <span className="text-sm font-medium leading-snug">{sc.intitule}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-3 pt-2 pb-4 space-y-3">
                          {/* Objectif */}
                          <Section icon={Target} title="Objectif d'apprentissage" color={COMP_COLORS[c.code]}>
                            <p className="text-sm leading-relaxed font-medium">{sc.objectif}</p>
                          </Section>

                          {/* Critères de réalisation */}
                          <Section icon={ListChecks} title="Critères de réalisation (ce qui est observable)" color={COMP_COLORS[c.code]}>
                            <ul className="space-y-1 text-sm">
                              {sc.criteres.map((crit, i) => (
                                <li key={i} className="flex gap-2">
                                  <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: COMP_COLORS[c.code] }} />
                                  {crit}
                                </li>
                              ))}
                            </ul>
                          </Section>

                          {/* Leçon à donner */}
                          <Section icon={GraduationCap} title="Leçon pour bien progresser" color={COMP_COLORS[c.code]} highlight>
                            <div className="space-y-3 text-sm">
                              <div>
                                <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1">Contexte</div>
                                <p className="leading-relaxed">{sc.lecon.contexte}</p>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase font-bold text-muted-foreground mb-1">Démarche pédagogique</div>
                                <ol className="space-y-1.5">
                                  {sc.lecon.demarche.map((step, i) => (
                                    <li key={i} className="flex gap-2 items-start">
                                      <span
                                        className="flex-shrink-0 w-5 h-5 rounded-full text-[10px] font-bold text-white flex items-center justify-center mt-0.5"
                                        style={{ backgroundColor: COMP_COLORS[c.code] }}
                                      >
                                        {i + 1}
                                      </span>
                                      <span className="leading-relaxed">{step}</span>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                              <div className="bg-primary/5 border border-primary/20 rounded-md px-3 py-2 italic flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                                <span className="text-sm">« {sc.lecon.phraseClef} »</span>
                              </div>
                            </div>
                          </Section>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* CTA "Articuler le livret" — RÉSERVÉ ENSEIGNANT/ADMIN (Phase 31)
            Les élèves ne doivent pas voir de renvoi vers le contenu prépa jury CCS. */}
        {isTeacherOrAdmin && (
          <Card className="bg-gradient-to-br from-primary/5 to-background border-primary/20 mt-6">
            <CardContent className="py-6 text-center space-y-3">
              <h2 className="text-lg font-semibold">Articuler le livret avec le reste de la formation</h2>
              <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
                Le livret REMC est l&apos;épine dorsale de la formation moto.
                Couplez-le aux fondements pédagogiques (méthodes, évaluation) et au référentiel CCS pour préparer le jury.
              </p>
              <div className="flex flex-wrap justify-center gap-2 pt-2">
                <Button asChild size="sm" variant="default" data-testid="cta-fondements">
                  <Link to="/enseignant/fondements-pedagogie">🧠 Fondements pédagogiques</Link>
                </Button>
                <Button asChild size="sm" variant="outline" data-testid="cta-referentiel">
                  <Link to="/enseignant/referentiel">📖 Référentiel CCS</Link>
                </Button>
                <Button asChild size="sm" variant="outline" data-testid="cta-etudes">
                  <Link to="/enseignant/etudes-de-cas">📚 27 études de cas</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children, color = 'hsl(var(--primary))', highlight = false }) {
  return (
    <div className={`rounded-md p-3 ${highlight ? 'bg-muted/40 border' : 'bg-muted/20 border border-border/50'}`} style={highlight ? { borderColor: color + '40' } : {}}>
      <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-2" style={{ color }}>
        <Icon className="w-3.5 h-3.5" />
        {title}
      </h3>
      <div>{children}</div>
    </div>
  );
}
